import React, { useEffect, useState, useRef } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Modal, Button } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Spinner from "../components/Common/Spinner";
import {
  createNewAdmin,
  deleteAdmin,
  getSubAdmins,
  updateAdminInfo,
  updateAdminStatus,
  uploadBulkUsers, // <-- Add this import
} from "../../services/Admin/adminServices";
import Pagination from "../components/Pagination";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { uploadFileToS3 } from "../../../lib/S3Bucket";
import { IdCardLanyardIcon } from "lucide-react";

const Users = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [date, setDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState({});
  const [sortBy, setSortBy] = useState("");
  
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const fileInputRef = useRef(null);
const [uploadResults, setUploadResults] = useState(null);
const [showResultsModal, setShowResultsModal] = useState(false);
  // --- NEW BULK UPLOAD STATES ---
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const bulkFileInputRef = useRef(null);

  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await getSubAdmins(
        currentPage,
        itemsPerPage,
        debouncedSearch,
        sortBy,
        date,
        endDate
      );
      setAdmins(response?.data?.data?.users || []);
      setTotalItems(response?.data?.data?.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [currentPage, debouncedSearch, sortBy, date, endDate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (selectedAdmin?.image) {
      setPreview(selectedAdmin.image);
    } else {
      setPreview("");
    }
  }, [selectedAdmin]);

  const openModal = (admin = null) => {
    setSelectedAdmin(admin);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    setPreview("");
    setShowPassword(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openConfirmModal = (action, data) => {
    console.log("action",action)
    setConfirmAction(action);
    setConfirmData(data);
    setConfirmModal(true);
  };

  const handleConfirm = async () => {
    try {
      if (confirmAction === "delete") {
        await deleteAdmin(confirmData.id);
        toast.success("User deleted successfully");
      }
         if (confirmAction === "recover") {
         const payload ={
          userId:confirmData.id,
          isDeleted:false
         }
          await updateAdminInfo(payload);
        toast.success("User revoer successfully");
      }
      if (confirmAction === "status") {
        await updateAdminStatus({ userId: confirmData.id });
        toast.success("Status updated successfully");
      }

      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Action failed");
    } finally {
      setConfirmModal(false);
      setConfirmAction(null);
      setConfirmData({});
    }
  };

  const AdminSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phoneNumber: Yup.string().required("Phone Number is required"),
    password: Yup.string().when([], {
      is: () => !selectedAdmin,
      then: (schema) => schema.required("Password is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      if (selectedAdmin) {
        await updateAdminInfo(values);
        toast.success("User updated successfully");
      } else {
        await createNewAdmin(values);
        toast.success("User added successfully");
      }
      fetchAdmins();
      handleCloseModal();
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Operation failed");
    }
  };

  const handleFileUpload = async (e, fieldName, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    try {

       
      setUploading(true);
      const key = `uploads/${Date.now()}-${file.name}`;
      const res = await uploadFileToS3(file, key);

      if (res) {
        setPreview(URL.createObjectURL(file));
        setFieldValue(fieldName, res);
      }
    } catch (error) {
      toast.error("File upload failed");
      setPreview("");
    } finally {
      setUploading(false);
    }
  };

  // --- NEW BULK UPLOAD HANDLERS ---
  const handleCloseBulkModal = () => {
    setShowBulkModal(false);
    setBulkFile(null);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
  };

  const handleBulkSubmit = async () => {
    if (!bulkFile) {
      toast.error("Please select a CSV file to upload.");
      return;
    }
console.log("Selected Bulk File:", bulkFile);
    const formData = new FormData();
    formData.append("file", bulkFile); // Make sure 'file' matches your backend's expected field name

    try {
      setUploadingBulk(true);
     const res = await uploadBulkUsers(formData);
     const responseData = res?.data?.data;
     if (responseData?.failed>0) {
     
      setUploadResults(responseData);
      setShowResultsModal(true);
      handleCloseBulkModal();
    } else {
      toast.success("Bulk users uploaded successfully!");
      fetchAdmins();
      handleCloseBulkModal();
    }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Bulk upload failed.");
    } finally {
      setUploadingBulk(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 style={{ color: "#87C55F" }}>Users</h2>
        <div className="d-flex justify-content-between gap-1 align-items-center mb-3">
          <Button
            style={{ backgroundColor: "#87C55F", border: "none" }}
            onClick={() => openModal()}
          >
            Add Registered Users
          </Button>
          <Button
            style={{ backgroundColor: "#87C55F", border: "none" }}
            onClick={() => setShowBulkModal(true)} // <-- Added onClick handler here
          >
            Bulk Upload
          </Button>
        </div>
      </div>

      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-end mb-4">
        <div className="flex-grow-1">
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#87C55F" }}
            className="form-control w-100"
            placeholder="Search by name, email....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="d-flex flex-column">
          <label className="mb-1" style={{ fontSize: "0.85rem", fontWeight: "500", color: "#555" }}>
            Start Date
          </label>
          <input
            type="date"
            style={{ borderRadius: "0.5rem", borderColor: "#87C55F" }}
            className="form-control w-100"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="d-flex flex-column">
          <label className="mb-1" style={{ fontSize: "0.85rem", fontWeight: "500", color: "#555" }}>
            End Date
          </label>
          <input
            type="date"
            style={{ borderRadius: "0.5rem", borderColor: "#87C55F" }}
            className="form-control w-100"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="d-flex flex-column">
          <label className="mb-1" style={{ fontSize: "0.85rem", fontWeight: "500", color: "#555" }}>
            User Type
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ borderRadius: "0.5rem", borderColor: "#87C55F" }}
            className="form-control w-100"
          >
            <option value="">All Users</option>
            <option value="true">Registered</option>
            <option value="false">Not Registered</option>
          </select>
        </div>

        {(search || sortBy || date || endDate) && (
          <div>
            <button
              type="button"
              className="btn text-white"
              style={{ borderRadius: "0.5rem", backgroundColor: "#dc3545" }}
              onClick={() => {
                setSearch("");
                setDate("");
                setEndDate("");
                setSortBy("");
                setCurrentPage(0);
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <table className="table table-striped align-middle shadow-sm">
          <thead className="table-light">
            <tr>
              <th>Sr no.</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone Number</th>
              <th>Joined Date</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {admins?.length > 0 ? (
              admins.map((admin, index) => (
                <tr
                  key={admin._id || index}
                  className={admin.isDeleted ? "table-danger" : ""}
                  style={admin.isDeleted ? { backgroundColor: "#f8d7da" } : {}}
                >
                  <td>{currentPage * itemsPerPage + index + 1}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <img
                        src={
                          admin.image ||
                          `https://ui-avatars.com/api/?name=${admin.name}&background=random`
                        }
                        alt={admin.name}
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginRight: "12px",
                          border: "1px solid #ddd",
                        }}
                      />
                      <span className="fw-medium">{admin.name}</span>
                    </div>
                  </td>
                  <td>{admin.email}</td>
                  <td>{admin.phoneNumber ? `${admin.countryCode}${admin.phoneNumber}` : "_"}</td>
                  <td>
                    {new Date(admin.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                    {", "}
                    {new Date(admin.createdAt).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td>
                    <span className={`badge ${!admin.isBlocked ? "bg-success" : "bg-danger"}`}>
                      {!admin.isBlocked ? "Active" : "Suspended"}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Dropdown align="end">
                      <Dropdown.Toggle variant="light" id={`dropdown-basic-${index}`} style={{ padding: "4px 8px" }}>
                        {svg1}
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={() => openModal(admin)}>Edit</Dropdown.Item>
                        <Dropdown.Item
                          onClick={() =>
                            openConfirmModal("status", {
                              id: admin._id,
                              name: admin.name,
                              isBlocked: admin.isBlocked,
                            })
                          }
                        >
                          {admin.isBlocked ? "Enable" : "Suspend"}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() =>
                            openConfirmModal("delete", {
                              id: admin._id,
                              name: admin.name,
                            })
                          }
                        >
                          Delete
                        </Dropdown.Item>
                        {admin.isDeleted &&
                             <Dropdown.Item
                          onClick={() =>
                            openConfirmModal("recover", {
                              id: admin._id,
                              name: admin.name,
                            })
                          }
                        >
                          Recover
                        </Dropdown.Item>}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center">
                  No user found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {admins?.length > 0 && (
        <div className="card-footer clearfix">
          <div className="d-flex justify-content-between align-items-center">
            <div className="dataTables_info" style={{ color: "#87C55F" }}>
              Showing {currentPage * itemsPerPage + 1} to{" "}
              {totalItems > (currentPage + 1) * itemsPerPage ? (currentPage + 1) * itemsPerPage : totalItems} of {totalItems} entries
            </div>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Pagination pageCount={totalPages} pageValue={currentPage} setPage={setCurrentPage} />
            </div>
          </div>
        </div>
      )}

      {/* Existing Edit/Add Modal */}
      <Modal size="xl" show={showModal} onHide={handleCloseModal}>
        {/* ... (Your existing Modal content remains unchanged) ... */}
        <Modal.Header closeButton>
          <Modal.Title>{selectedAdmin ? "Edit User" : "Add User"}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={
            selectedAdmin
              ? {
                  userId: selectedAdmin._id,
                  name: selectedAdmin.name || "",
                  email: selectedAdmin.email || "",
                  phoneNumber: selectedAdmin.phoneNumber || "",
                  countryCode: selectedAdmin.countryCode || "+91",
                  image: selectedAdmin.image || "",
                }
              : {
                  name: "",
                  email: "",
                  phoneNumber: "",
                  countryCode: "+91",
                  image: "",
                  password: "",
                }
          }
          enableReinitialize
          validationSchema={AdminSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <FormikForm>
              <Modal.Body>
                {/* Your form fields */}
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <Field name="name" placeholder="ex. John Doe" className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`} />
                  <ErrorMessage name="name" component="div" className="text-danger" />
                </div>

                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <Field name="email" type="email" placeholder="example@gmail.com" className={`form-control ${errors.email && touched.email ? "is-invalid" : ""}`} />
                  <ErrorMessage name="email" component="div" className="text-danger" />
                </div>

                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <PhoneInput
                    country={"us"}
                    value={`${values.countryCode}${values.phoneNumber}`}
                    onChange={(value, country) => {
                      let dialCode = country.dialCode;
                      let onlyNumber = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;
                      onlyNumber = onlyNumber.replace(/^0+/, "");
                      setFieldValue("countryCode", "+" + dialCode);
                      setFieldValue("phoneNumber", onlyNumber);
                    }}
                    inputClass="form-control w-100 w-md-50"
                    style={{ borderColor: "#0AB7EA" }}
                  />
                  <ErrorMessage name="phoneNumber" component="div" className="text-danger" />
                </div>

                <div className="mb-3">
                  <label className="form-label">Image</label>
                  <input type="file" accept="image/*" className="form-control" ref={fileInputRef} onChange={(e) => handleFileUpload(e, "image", setFieldValue)} />
                   <small className="text-muted d-block mt-1">
                  Max file size: 5MB. Supported formats: JPG, PNG, GIF, WebP
                </small>
                </div>

                {uploading ? (
                  <div className="text-success mt-2">Uploading image...</div>
                ) : (
                  preview && (
                    <div className="mb-3 text-center mt-3">
                      <div style={{ display: "inline-block", position: "relative" }}>
                        <img
                          src={preview}
                          alt="preview"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "10px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm shadow"
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: "1",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                          onClick={() => {
                            setPreview("");
                            if (fileInputRef.current) fileInputRef.current.value = "";
                            setFieldValue("image", "");
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  )
                )}

                {!selectedAdmin && (
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <div className="input-group">
                      <Field name="password" type={showPassword ? "text" : "password"} className={`form-control ${errors.password && touched.password ? "is-invalid" : ""}`} />
                      <span className="input-group-text" style={{ cursor: "pointer", background: "#fff" }} onClick={() => setShowPassword(!showPassword)}>
                        <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </span>
                    </div>
                    <ErrorMessage name="password" component="div" className="text-danger" />
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Close
                </Button>
                <Button type="submit" style={{ backgroundColor: "#87C55F", border: "none" }} disabled={uploading}>
                  {selectedAdmin ? "Save Changes" : "Add Admin"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      {/* --- NEW BULK UPLOAD MODAL --- */}
      <Modal show={showBulkModal} onHide={handleCloseBulkModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Upload Users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Upload CSV File</label>
            <input
              type="file"
              accept=".csv"
              className="form-control"
              ref={bulkFileInputRef}
              onChange={(e) => setBulkFile(e.target.files[0])}
            />
            <small className="text-muted d-block mt-2">
              Ensure your CSV contains columns for <strong>email, name, password</strong>.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseBulkModal} disabled={uploadingBulk}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "#87C55F", border: "none" }}
            onClick={handleBulkSubmit}
            disabled={uploadingBulk || !bulkFile}
          >
            {uploadingBulk ? "Uploading..." : "Upload CSV"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Existing Confirm Modal */}
      <Modal show={confirmModal} onHide={() => setConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmAction === "delete" ? "Confirm Delete" : "Confirm Action"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
         {confirmAction === "delete" ? (
  <p>
    Are you sure you want to delete <strong>{confirmData.name}</strong>?
  </p>
) : confirmAction === "recover" ? (
  <p>
    Are you sure you want to recover <strong>{confirmData.name}</strong>?
  </p>
) : (
  <p>
    Are you sure you want to{" "}
    <strong>{confirmData.isBlocked ? "Enable" : "Suspend"}</strong>{" "}
    <strong>{confirmData.name}</strong>?
  </p>
)}
        </Modal.Body>
        <Modal.Footer>
          <Button style={{ backgroundColor: "#87C55F", border: "none" }} onClick={handleConfirm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --- BULK UPLOAD RESULTS MODAL --- */}
<Modal 
  show={showResultsModal} 
  onHide={() => {
    setShowResultsModal(false);
    setUploadResults(null);
    fetchAdmins(); // Refresh table when they close the results
  }} 
  centered 
  size="lg"
>
  <Modal.Header closeButton>
    <Modal.Title>Bulk Upload Summary</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {uploadResults && (
      <div>
        {/* Summary Banner */}
        <div className={`alert ${uploadResults.failed > 0 ? 'alert-warning' : 'alert-success'} d-flex justify-content-between`}>
          <div><strong>Total Processed:</strong> {uploadResults.total}</div>
          <div><strong>Inserted:</strong> <span className="text-success fw-bold">{uploadResults.inserted}</span></div>
          <div><strong>Failed:</strong> <span className={uploadResults.failed > 0 ? "text-danger fw-bold" : ""}>{uploadResults.failed}</span></div>
        </div>

        {/* Failed Entries Table */}
        {uploadResults.failed > 0 && uploadResults.errors?.length > 0 && (
          <div className="mt-4">
            <h6 className="text-danger mb-3">Failed Entries Details:</h6>
            <div className="table-responsive border" style={{ maxHeight: '300px', overflowY: 'auto', borderRadius: '0.5rem' }}>
              <table className="table table-striped table-hover mb-0">
                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Row</th>
                    <th>Email</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResults.errors.map((errorItem, index) => (
                    <tr key={index}>
                      <td>{errorItem.row}</td>
                      <td className="fw-medium">{errorItem.email}</td>
                      <td className="text-danger">{errorItem.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )}
  </Modal.Body>
  <Modal.Footer>
    <Button 
      style={{ backgroundColor: "#87C55F", border: "none" }} 
      onClick={() => {
        setShowResultsModal(false);
        setUploadResults(null);
        fetchAdmins(); // Refresh table 
      }}
    >
      Acknowledge & Refresh
    </Button>
  </Modal.Footer>
</Modal>
    </div>
  );
};

export default Users;